import PropTypes from 'prop-types';
import React, { useState, useMemo } from 'react';
import Popup from "reactjs-popup";
import Tippy from '@tippyjs/react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import CurrencyInput from '../../CurrencyInput/components/CurrencyInput';
import AllInPriceModal from './AllInPriceModal';
import { createTabIndexCalculator, createTicketsSectionColumns } from '../utils/tabIndexHelper';
import { useDebouncedPerKeySave } from '../hooks/useDebouncedPerKeySave';
import useIsMobile from '../../shared/hooks/useIsMobile';
import useFieldDraft from '../hooks/useFieldDraft';
import { ticketQtyChanges, formatAmountForDisplay } from '../utils/budgetFieldValue';

const restrictToVerticalAxis = ({ transform, containerNodeRect, draggingNodeRect }) => {
  const clampedTransform = { ...transform, x: 0 };
  if (containerNodeRect && draggingNodeRect) {
    const topBound = containerNodeRect.top - draggingNodeRect.top;
    const bottomBound = containerNodeRect.top + containerNodeRect.height - draggingNodeRect.top - draggingNodeRect.height;
    clampedTransform.y = Math.min(Math.max(clampedTransform.y, topBound), bottomBound);
  }
  return clampedTransform;
};

const dragHandleStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  width: '14px',
  flexShrink: 0,
  cursor: 'grab',
  color: 'var(--bk-c-717680, #717680)',
  fontSize: '12px',
};

const SortableRow = ({ id, children, style }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id });

  const sortableStyle = {
    ...style,
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div ref={setNodeRef} style={sortableStyle} {...attributes}>
      <div style={dragHandleStyle} {...listeners}>
        <i className="far fa-grip-dots-vertical"></i>
      </div>
      {children}
    </div>
  );
};

const inlineMenuContentStyle = {
  textAlign: "left",
  border: "none",
  borderRadius: "8px",
  boxShadow: "0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)",
  width: "215px",
  padding: "0px"
};

const sectionHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px'
};

const sectionTitleStyle = {
  fontSize: '18px',
  fontWeight: '600',
  color: 'var(--bk-c-333333, #333333)',
  margin: 0
};

const contentContainerStyle = {
  display: 'flex',
  gap: '8px',
  padding: '0 16px 16px 16px',
  overflowX: 'auto'
};

const collapseButtonStyle = {
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: '10px',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  outline: 'none'
};

const inputLabelStyle = {
  fontSize: '12px',
  fontWeight: '500',
  color: 'var(--bk-c-414651, #414651)',
  marginBottom: '0',
  display: 'block'
};

// Retained for the Total columns only. Every other numeric field clears on focus
// when it holds zero (O-4781, handleZeroFocus), but Total is derived
// from qty x price with its own override state machine (editingTotal): emptying
// it would commit a blank override rather than clear an entry.
const handleSelectOnFocus = (e) => e.target.select();

const inputStyle = {
  backgroundColor: 'var(--bk-c-ffffff, #ffffff)',
  border: '1px solid var(--bk-c-d5d7da, #D5D7DA)',
  borderRadius: '0',
  padding: '8px 8px',
  fontSize: '14px',
  color: 'var(--bk-c-333333, #333333)',
  boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
  width: '100%',
  boxSizing: 'border-box'
};

const disabledInputStyle = {
  ...inputStyle,
  backgroundColor: 'var(--bk-c-fafafa, #FAFAFA)',
  color: 'var(--bk-c-717680, #717680)'
};

const convertedTotalStyle = {
  fontSize: '12px',
  fontWeight: '500',
  color: 'var(--bk-c-717680, #717680)',
  textAlign: 'right',
  paddingRight: '8px'
};

const addButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  color: 'var(--bk-c-136395, #136395)',
  fontSize: '14px',
  cursor: 'pointer',
  background: 'none',
  border: 'none',
  padding: '8px 0',
  outline: 'none',
  width: 'fit-content',
};

const verticalTextStyle = {
  writingMode: 'vertical-rl',
  textOrientation: 'mixed',
  fontSize: '16px',
  fontWeight: '600',
  color: 'var(--bk-c-333333, #333333)',
  textAlign: 'center',
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const columnHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 0'
};

const columnTitleStyle = {
  fontSize: '16px',
  fontWeight: '600',
  color: 'var(--bk-c-333333, #333333)',
  paddingLeft: '8px'
};

const updatedBudgetWithBudgetLineItem = (budget, budgetLineItem) => {
  var ticketLineItems = [...budget.ticket_line_items].map((item) => {
    if (item.uuid === budgetLineItem.uuid) {
      return budgetLineItem;
    } else {
      return item;
    }
  });

  return Object.assign({}, budget, {
    ticket_line_items: ticketLineItems
  });
};

const updatedBudgetRemovingBudgetLineItem = (budget, budgetLineItem) => {
  var ticketLineItems = budget.ticket_line_items.filter((item) =>
    item.uuid !== budgetLineItem.uuid
  );

  return Object.assign({}, budget, {
    ticket_line_items: ticketLineItems
  });
};

const inlineRowIconBtnStyle = {
  background: 'transparent',
  border: 'none',
  padding: '6px',
  cursor: 'pointer',
  color: 'var(--bk-c-6b7280, #6B7280)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const inlineRowIconDangerBtnStyle = {
  ...inlineRowIconBtnStyle,
  color: 'var(--bk-c-d92d20, #D92D20)',
};

export const TicketRowMenu = ({
  csrfToken,
  team,
  budget,
  budgetLineItem,
  updateBudget,
  autoSaveBudgetLineItem,
  onDeleteRow
}) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    const toggleVisibility = () => {
      const updatedBudgetLineItem = {
        ...budgetLineItem,
        show_in_offer: !budgetLineItem.show_in_offer,
      };
      const updatedBudget = updatedBudgetWithBudgetLineItem(budget, updatedBudgetLineItem);
      updateBudget(updatedBudget);
      autoSaveBudgetLineItem(csrfToken, team, updatedBudgetLineItem);
    };

    return (
      <div style={{ display: 'flex', gap: '2px' }}>
        <button
          type="button"
          aria-label={budgetLineItem.show_in_offer ? "Don't show in offer" : 'Show in offer'}
          onClick={(e) => { e.preventDefault(); toggleVisibility(); }}
          style={inlineRowIconBtnStyle}
        >
          <i className={`fas ${budgetLineItem.show_in_offer ? 'fa-eye-slash' : 'fa-eye'}`} />
        </button>
        <button
          type="button"
          aria-label="Remove row"
          onClick={(e) => { e.preventDefault(); onDeleteRow(budgetLineItem); }}
          style={inlineRowIconDangerBtnStyle}
        >
          <i className="far fa-times-circle" />
        </button>
      </div>
    );
  }

  return (
    <Popup
      trigger={
        <a href="#" className="text-muted">
          <i className="fas fa-ellipsis-v"></i>
        </a>
      }
      onOpen={(e) => e.preventDefault()}
      arrow={false}
      offsetY={5}
      position="bottom right"
      contentStyle={inlineMenuContentStyle}
      closeOnDocumentClick
    >
      {close => (
        <div>
          <div className="row text-left">
            <div className="col">
              <ul className="list-group offer-inline-menu highlight-primary">
                {budgetLineItem.show_in_offer ? (
                  <li className="list-group-item">
                    <a href="#"
                       className="text-dark"
                       onClick={(e) => {
                         e.preventDefault();
                         var updatedBudgetLineItem = { ...budgetLineItem, show_in_offer: false };
                         var updatedBudget = updatedBudgetWithBudgetLineItem(
                           budget, updatedBudgetLineItem);
                         updateBudget(updatedBudget);
                         autoSaveBudgetLineItem(csrfToken, team, updatedBudgetLineItem);
                         close();
                       }}>
                      <i className="text-muted fas fa-eye-slash fa-fw mx-2"></i>
                      <span style={{ fontWeight: 'normal' }}>
                        Don't show in offer
                      </span>
                    </a>
                  </li>
                ) : (
                  <li className="list-group-item">
                    <a href="#"
                       className="text-dark"
                       onClick={(e) => {
                         e.preventDefault();
                         var updatedBudgetLineItem = { ...budgetLineItem, show_in_offer: true };
                         var updatedBudget = updatedBudgetWithBudgetLineItem(
                           budget, updatedBudgetLineItem);
                         updateBudget(updatedBudget);
                         autoSaveBudgetLineItem(csrfToken, team, updatedBudgetLineItem);
                         close();
                       }}>
                      <i className="text-muted fas fa-eye fa-fw mx-2"></i>
                      <span style={{ fontWeight: 'normal' }}>
                        Show in offer
                      </span>
                    </a>
                  </li>
                )}
                <li className="list-group-item delete-offer-li border-0">
                  <a href="#"
                     onClick={(e) => {
                       e.preventDefault();
                       onDeleteRow(budgetLineItem);
                       close();
                     }}
                     className="text-danger">
                    <i className="far fa-times-circle fa-fw mx-2"></i>
                    <span style={{ fontWeight: 'normal' }}>
                      Remove row
                    </span>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </Popup>
  );
};

const mergeTicketsWithSettlement = (budgetTickets, settlementTickets) => {
  const result = budgetTickets.map(ticket => ({ ...ticket }));
  const matchedBudgetUuids = new Set();

  if (settlementTickets && settlementTickets.length > 0) {
    const matchedSettlementUuids = new Set();

    settlementTickets.forEach(settlementTicket => {
      const matchIndex = result.findIndex(budgetTicket =>
        budgetTicket.name === settlementTicket.name &&
        parseFloat(budgetTicket.left_hand) === parseFloat(settlementTicket.left_hand)
      );

      if (matchIndex !== -1) {
        result[matchIndex] = {
          ...result[matchIndex],
          left_hand_actual: settlementTicket.left_hand,
          right_hand_actual: settlementTicket.right_hand
        };
        matchedSettlementUuids.add(settlementTicket.uuid);
        matchedBudgetUuids.add(result[matchIndex].uuid);
      }
    });

    settlementTickets.forEach(settlementTicket => {
      if (!matchedSettlementUuids.has(settlementTicket.uuid)) {
        result.push({
          uuid: settlementTicket.uuid,
          name: settlementTicket.name,
          left_hand: settlementTicket.left_hand,
          left_hand_forecast: settlementTicket.left_hand,
          isFromSettlement: true,
          available: null,
          comps: null,
          kills: null,
          right_hand: null,
          right_hand_forecast: null,
          left_hand_actual: settlementTicket.left_hand,
          right_hand_actual: settlementTicket.right_hand
        });
      }
    });
  }

  result.forEach((ticket, index) => {
    if (!matchedBudgetUuids.has(ticket.uuid) && !ticket.isFromSettlement) {
      const priceFromOffer = ticket.left_hand || ticket.left_hand_forecast || 0;
      if (!ticket.left_hand_actual || parseFloat(ticket.left_hand_actual) === 0) {
        result[index] = {
          ...ticket,
          left_hand_actual: priceFromOffer
        };
      }
    }
  });

  return result;
};

const TicketsSection = ({
  csrfToken,
  team,
  currentUser,
  offer,
  budget,
  calculated,
  updateBudget,
  createTicketBudgetLineItem,
  deleteBudgetLineItem,
  autoSaveBudgetLineItem,
  formatCalculatedNumber,
  expanded,
  onToggleExpand,
  settlementTickets = [],
  showOfferColumn = true,
  showForecastColumn = true,
  showActualsColumn = true,
  hideCollapsedColumns = false,
  onColumnVisibilityChange,
  sectionTabIndexOffset = 0,
  showCurrencyConversion,
  globalCurrency,
  reorderLineItem,
  onReorderLineItems,
  allLineItems: allLineItemsFromContext
}) => {
  const [editingTotal, setEditingTotal] = useState(null); // { uuid, fieldPrefix, value }

  const {
    isCleared, zeroFocus: handleZeroFocus, release: releaseClearedField,
    displayValue, handleFocus: handlePriceFocus, handleBlur, handleInput,
  } = useFieldDraft();
  const handleZeroBlur = (item, field) => () => releaseClearedField(item, field);

  const priceDisplayValue = (item, field, displayedValue) =>
    displayValue(item, field, displayedValue, formatAmountForDisplay);

  const handlePriceBlur = (item, field) => () => handleBlur(item, field);

  const handlePriceInput = (item, field, displayedValue) =>
    handleInput(item, field, displayedValue, formatAmountForDisplay, (typed) => handleFieldChange(item, field, typed));
  const [allInOpen, setAllInOpen] = useState(false);
  const isMobile = useIsMobile();

  const showOffer = showOfferColumn;
  const showForecast = showForecastColumn;
  const showActuals = showActualsColumn;

  // Helper to get the override field name for a given column
  const getOverrideFieldName = (fieldPrefix) => {
    switch (fieldPrefix) {
      case '': return 'total_override';
      case '_forecast': return 'total_forecast_override';
      case '_actual': return 'total_actual_override';
      default: return 'total_override';
    }
  };

  // Helper to check for rounding mismatch
  const hasRoundingMismatch = (budgetLineItem, fieldPrefix, calculatedRow) => {
    const overrideFieldName = getOverrideFieldName(fieldPrefix);
    const overrideValue = budgetLineItem[overrideFieldName];

    if (overrideValue == null || overrideValue === '') return false;

    const calculatedTotal = fieldPrefix === ''
      ? calculatedRow?.calculatedTotal
      : fieldPrefix === '_forecast'
        ? calculatedRow?.calculatedForecastTotal
        : calculatedRow?.calculatedActualTotal;

    if (calculatedTotal == null) return false;

    return Math.abs(parseFloat(overrideValue) - calculatedTotal) > 0.01;
  };

  const debouncedAutoSave = useDebouncedPerKeySave(autoSaveBudgetLineItem);

  const budgetTicketLineItems = budget.ticket_line_items || [];
  const ticketLineItems = mergeTicketsWithSettlement(
    budgetTicketLineItems, settlementTickets);

  const hasCalculated = calculated && Object.keys(calculated).length > 0;

  const ticketsTotal = hasCalculated ? calculated.ticketsSection.total : 0;
  const ticketsForecastTotal = hasCalculated
    ? calculated.ticketsSection.forecastTotal : 0;
  const ticketsActualsTotal = hasCalculated
    ? calculated.ticketsSection.actualTotal : 0;

  const ticketsSellableTotal = hasCalculated
    ? calculated.ticketsSection.rightTotal : 0;
  const ticketsForecastQtyTotal = hasCalculated
    ? calculated.ticketsSection.rightForecastTotal : 0;
  const ticketsActualsQtyTotal = hasCalculated
    ? calculated.ticketsSection.rightActualTotal : 0;

  const ticketsAvailableTotal = hasCalculated
    ? calculated.ticketsSection.items
        .filter(i => i.show_in_offer !== false)
        .reduce((sum, i) => sum + Number(i.available || 0), 0)
    : 0;
  const ticketsCompsTotal = hasCalculated
    ? calculated.ticketsSection.items
        .filter(i => i.show_in_offer !== false)
        .reduce((sum, i) => sum + Number(i.comps || 0), 0)
    : 0;
  const ticketsKillsTotal = hasCalculated
    ? calculated.ticketsSection.items
        .filter(i => i.show_in_offer !== false)
        .reduce((sum, i) => sum + Number(i.kills || 0), 0)
    : 0;

  const handleAddRow = () => {
    createTicketBudgetLineItem(
      csrfToken,
      team,
      budget,
      (ticketLineItems.length + 1)
    );
  };

  const handleDeleteRow = (budgetLineItem) => {
    var updated = updatedBudgetRemovingBudgetLineItem(budget, budgetLineItem);
    updateBudget(updated);
    deleteBudgetLineItem(csrfToken, team, budgetLineItem);
  };

  // Drag and drop reorder
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const sortableIds = ticketLineItems.map(li => li.uuid || String(li.id));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!active || !over || active.id === over.id || !reorderLineItem) return;

    const oldIndex = ticketLineItems.findIndex(li => (li.uuid || String(li.id)) === active.id);
    const newIndex = ticketLineItems.findIndex(li => (li.uuid || String(li.id)) === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(ticketLineItems, oldIndex, newIndex);
    const movedItem = reordered[newIndex];

    // Optimistic update: assign new positions
    const reorderedPositions = {};
    reordered.forEach((item, i) => { reorderedPositions[item.uuid] = i + 1; });

    const updatedAllLineItems = (allLineItemsFromContext || []).map(li => {
      if (reorderedPositions[li.uuid] !== undefined) {
        return { ...li, position: reorderedPositions[li.uuid] };
      }
      return li;
    });

    onReorderLineItems(updatedAllLineItems);
    reorderLineItem(movedItem.id, newIndex + 1);
  };

  const numericFields = [
    'left_hand', 'left_hand_forecast', 'left_hand_actual',
    'right_hand', 'right_hand_forecast', 'right_hand_actual',
    'available', 'comps', 'kills'
  ];

  const handleFieldChange = (budgetLineItem, field, value) => {
    // The focus-clear ends at the first keystroke, not at blur — while it is held
    // the input renders '', so keeping it would re-blank the field on every render
    // and each onChange would carry only the character just typed.
    releaseClearedField(budgetLineItem, field);
    const safeValue = numericFields.includes(field) && (value === '' || value == null) ? 0 : value;
    var updatedBudgetLineItem = {
      ...budgetLineItem,
      [field]: safeValue
    };

    if (field === 'available' || field === 'comps' || field === 'kills') {
      const qtyChanges = ticketQtyChanges(budgetLineItem, field, value);
      Object.assign(updatedBudgetLineItem, qtyChanges);
      // Clear offer override since qty changed
      updatedBudgetLineItem.total_override = null;
      // A following forecast qty was carried along, so clear its override too
      if ('right_hand_forecast' in qtyChanges) {
        updatedBudgetLineItem.total_forecast_override = null;
      }
      // Clear editingTotal if it was for this row's offer column
      if (editingTotal && editingTotal.uuid === budgetLineItem.uuid && editingTotal.fieldPrefix === '') {
        setEditingTotal(null);
      }
    }

    if (field === 'left_hand') {
      const oldLeftHand = budgetLineItem.left_hand;
      const forecastMatchesOffer = !budgetLineItem.left_hand_forecast ||
        budgetLineItem.left_hand_forecast === oldLeftHand;
      const actualsMatchesOffer = !budgetLineItem.left_hand_actual ||
        budgetLineItem.left_hand_actual === oldLeftHand;

      if (forecastMatchesOffer) {
        updatedBudgetLineItem.left_hand_forecast = value;
        // Also clear forecast override since price is synced
        updatedBudgetLineItem.total_forecast_override = null;
        // Clear editingTotal if it was for this row's forecast column
        if (editingTotal && editingTotal.uuid === budgetLineItem.uuid && editingTotal.fieldPrefix === '_forecast') {
          setEditingTotal(null);
        }
      }
      if (actualsMatchesOffer) {
        updatedBudgetLineItem.left_hand_actual = value;
        // Also clear actuals override since price is synced
        updatedBudgetLineItem.total_actual_override = null;
        // Clear editingTotal if it was for this row's actuals column
        if (editingTotal && editingTotal.uuid === budgetLineItem.uuid && editingTotal.fieldPrefix === '_actual') {
          setEditingTotal(null);
        }
      }
      // Clear offer override since price changed
      updatedBudgetLineItem.total_override = null;
      // Clear editingTotal if it was for this row's offer column
      if (editingTotal && editingTotal.uuid === budgetLineItem.uuid && editingTotal.fieldPrefix === '') {
        setEditingTotal(null);
      }
    }

    // When forecast qty changes, auto-populate forecast price from offer price if empty
    if (field === 'right_hand_forecast') {
      const forecastPrice = updatedBudgetLineItem.left_hand_forecast;
      if (!forecastPrice || forecastPrice === 0 || forecastPrice === '0' || forecastPrice === '') {
        updatedBudgetLineItem.left_hand_forecast = budgetLineItem.left_hand;
      }
      updatedBudgetLineItem.total_forecast_override = null;
      // Clear editingTotal if it was for this row's forecast column
      if (editingTotal && editingTotal.uuid === budgetLineItem.uuid && editingTotal.fieldPrefix === '_forecast') {
        setEditingTotal(null);
      }
    }
    if (field === 'left_hand_forecast') {
      updatedBudgetLineItem.total_forecast_override = null;
      // Clear editingTotal if it was for this row's forecast column
      if (editingTotal && editingTotal.uuid === budgetLineItem.uuid && editingTotal.fieldPrefix === '_forecast') {
        setEditingTotal(null);
      }
    }

    // Clear actuals override when actuals qty or price changes
    if (field === 'right_hand_actual') {
      updatedBudgetLineItem.total_actual_override = null;
      // Clear editingTotal if it was for this row's actuals column
      if (editingTotal && editingTotal.uuid === budgetLineItem.uuid && editingTotal.fieldPrefix === '_actual') {
        setEditingTotal(null);
      }
    }
    if (field === 'left_hand_actual') {
      updatedBudgetLineItem.total_actual_override = null;
      // Clear editingTotal if it was for this row's actuals column
      if (editingTotal && editingTotal.uuid === budgetLineItem.uuid && editingTotal.fieldPrefix === '_actual') {
        setEditingTotal(null);
      }
    }

    var updatedBudget = updatedBudgetWithBudgetLineItem(budget, updatedBudgetLineItem);
    updateBudget(updatedBudget);
    if (!budgetLineItem.isFromSettlement) {
      const changes = {};
      Object.keys(updatedBudgetLineItem).forEach((k) => {
        if (updatedBudgetLineItem[k] !== budgetLineItem[k]) {
          changes[k] = updatedBudgetLineItem[k];
        }
      });
      debouncedAutoSave(csrfToken, team, budgetLineItem, changes);
    }
  };

  const handleNameBlur = (budgetLineItem, index, value) => {
    if (!value || value.length < 1) {
      var updatedBudgetLineItem = {
        ...budgetLineItem,
        name: ("Ticket #" + (index + 1))
      };
      var updatedBudget = updatedBudgetWithBudgetLineItem(budget, updatedBudgetLineItem);
      updateBudget(updatedBudget);
      if (!budgetLineItem.isFromSettlement) {
        autoSaveBudgetLineItem(csrfToken, team, updatedBudgetLineItem);
      }
    }
  };

  const parseNumber = (value) => {
    if (typeof value === 'string') {
      return value.length > 0 ? Number(value.replace(/,/g, '')) : '';
    }
    return value;
  };

  const getTicketQty = (budgetLineItem, fieldPrefix) => {
    if (fieldPrefix === '') {
      return Number(budgetLineItem.right_hand) || 0;
    } else if (fieldPrefix === '_forecast') {
      return Number(budgetLineItem.right_hand_forecast) || 0;
    } else {
      return Number(budgetLineItem.right_hand_actual) || 0;
    }
  };

  const isTotalDisabledForTicket = (budgetLineItem, fieldPrefix) => {
    if (budgetLineItem.isFromSettlement) return true;
    // Disable if qty is 0 (can't divide by zero)
    const qty = getTicketQty(budgetLineItem, fieldPrefix);
    if (qty === 0) return true;
    return false;
  };

  const handleTotalFocus = (budgetLineItem, fieldPrefix, calculatedRow) => {
    // If we're already editing this exact field, keep the current value
    if (editingTotal && editingTotal.uuid === budgetLineItem.uuid && editingTotal.fieldPrefix === fieldPrefix) {
      return; // Don't reset, keep the user's entered value
    }
    // Otherwise, clear any previous editing state and start fresh
    setEditingTotal({
      uuid: budgetLineItem.uuid,
      fieldPrefix,
      value: null // null means "use input's current value, don't override"
    });
  };

  const handleTotalInputChange = (uuid, fieldPrefix, value) => {
    // Always update, using the uuid and fieldPrefix passed directly
    setEditingTotal({
      uuid,
      fieldPrefix,
      value
    });
  };

  const handleTotalBlur = (budgetLineItem, fieldPrefix, calculatedRow) => {
    if (!editingTotal) return;

    const totalValue = editingTotal.value;

    // If user didn't actually change the value (just focused and blurred), don't process
    if (totalValue === null || totalValue === undefined) {
      return;
    }

    const leftHandField = `left_hand${fieldPrefix}`;
    const overrideField = getOverrideFieldName(fieldPrefix);
    const parsedTotal = parseFloat(totalValue) || 0;

    // Get the quantity based on field prefix
    const qty = getTicketQty(budgetLineItem, fieldPrefix);

    // Calculate new price = total / qty
    const newPrice = qty > 0 ? (parsedTotal / qty) : 0;

    var changes = {
      [leftHandField]: newPrice.toFixed(2),
      [overrideField]: parsedTotal // Store the override to preserve user's exact entry
    };
    var updatedBudgetLineItem = { ...budgetLineItem, ...changes };

    var updatedBudget = updatedBudgetWithBudgetLineItem(budget, updatedBudgetLineItem);
    updateBudget(updatedBudget);
    if (!budgetLineItem.isFromSettlement) {
      debouncedAutoSave(csrfToken, team, budgetLineItem, changes);
    }
    // Don't clear editingTotal - keep showing the user's entered value
    // It will be cleared when they focus on a different total field
  };

  const isEditingTotalField = (uuid, fieldPrefix) => {
    return editingTotal && editingTotal.uuid === uuid && editingTotal.fieldPrefix === fieldPrefix;
  };

  const getCalculatedTotalValue = (fieldPrefix, calculatedRow) => {
    const calculatedTotal = fieldPrefix === ''
      ? calculatedRow?.calculatedTotal
      : fieldPrefix === '_forecast'
        ? calculatedRow?.calculatedForecastTotal
        : calculatedRow?.calculatedActualTotal;
    return calculatedTotal != null ? calculatedTotal.toFixed(2) : '';
  };

  const getTotalDisplayValue = (budgetLineItem, fieldPrefix, calculatedRow) => {
    // If currently editing this field and we have a user-entered value, show it
    if (isEditingTotalField(budgetLineItem.uuid, fieldPrefix) && editingTotal.value !== null) {
      return editingTotal.value;
    }
    // If there's an override value (user entered a total), show it
    const overrideField = getOverrideFieldName(fieldPrefix);
    const overrideValue = budgetLineItem[overrideField];
    if (overrideValue != null && overrideValue !== '') {
      return parseFloat(overrideValue).toFixed(2);
    }
    // Otherwise show the calculated value
    return getCalculatedTotalValue(fieldPrefix, calculatedRow);
  };

  const columnBoxStyle = {
    backgroundColor: 'var(--bk-c-fafafa, #FAFAFA)',
    border: '1px solid var(--bk-c-f5f5f5, #F5F5F5)',
    borderRadius: '8px',
    padding: '0 8px 8px 8px',
    flexShrink: 0
  };

  const collapsedColumnStyle = {
    borderRadius: '8px',
    padding: '8px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
    width: '56px',
    cursor: 'pointer'
  };

  const offerCollapsedStyle = {
    ...collapsedColumnStyle,
    backgroundColor: 'var(--bk-c-fafafa, #FAFAFA)',
    border: '1px solid var(--bk-c-f5f5f5, #F5F5F5)'
  };

  const forecastCollapsedStyle = {
    ...collapsedColumnStyle,
    backgroundColor: 'var(--bk-c-f6fafe, #F6FAFE)',
    border: '1px solid var(--bk-c-e4f2fc, #E4F2FC)'
  };

  const actualsCollapsedStyle = {
    ...collapsedColumnStyle,
    backgroundColor: 'var(--bk-c-eff4ff, #EFF4FF)',
    border: '1px solid var(--bk-c-d1e0ff, #D1E0FF)'
  };

  const forecastExpandedStyle = {
    ...columnBoxStyle,
    backgroundColor: 'var(--bk-c-f6fafe, #F6FAFE)',
    border: '1px solid var(--bk-c-e4f2fc, #E4F2FC)'
  };

  const actualsExpandedStyle = {
    ...columnBoxStyle,
    backgroundColor: 'var(--bk-c-eff4ff, #EFF4FF)',
    border: '1px solid var(--bk-c-d1e0ff, #D1E0FF)'
  };

  const rowHeight = 36;
  const headerHeight = 48;
  const labelRowHeight = 18;
  const labelMarginBottom = 6;
  const totalHeaderHeight = headerHeight + labelRowHeight + labelMarginBottom;

  const offerAvailableWidth = 72;
  const offerCompsWidth = 56;
  const offerKillsWidth = 56;
  const offerSellableWidth = 72;
  const offerSalePriceWidth = 80;
  const offerTotalWidth = 105;
  const convertedSalePriceWidth = 105;

  const forecastQtyWidth = 86;
  const forecastSalePriceWidth = 80;
  const forecastTotalWidth = 105;

  const actualsQtyWidth = 86;
  const actualsSalePriceWidth = 80;
  const actualsTotalWidth = 105;

  const getCalculatedRow = (budgetLineItem) => {
    if (hasCalculated && calculated.ticketsSection && calculated.ticketsSection.items) {
      return calculated.ticketsSection.items.find((item) => item.uuid === budgetLineItem.uuid);
    }
    return null;
  };

  const getTabIndex = useMemo(() => createTabIndexCalculator({
    sectionTabIndexOffset,
    columns: createTicketsSectionColumns({ showOffer, showForecast, showActuals })
  }), [sectionTabIndexOffset, showOffer, showForecast, showActuals]);

  return (
    <div style={{
      backgroundColor: 'var(--bk-c-ffffff, #ffffff)',
      border: '1px solid var(--bk-c-e9eaeb, #E9EAEB)',
      borderRadius: '8px',
      boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
      overflow: 'hidden'
    }}>
      <div style={{ ...sectionHeaderStyle, cursor: 'pointer' }} onClick={onToggleExpand}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <h3 style={sectionTitleStyle}>Tickets</h3>
          <Tippy
            content="Revenue from all manifested ticket sales. This amount appears on the offer and settlement and is included in all artist deal calculations."
            placement="top"
            theme="material"
            animation="shift-away"
          >
            <i className="fas fa-info-circle" style={{ fontSize: '14px', color: 'var(--bk-c-717680, #717680)', cursor: 'help', marginTop: '2px' }} onClick={(e) => e.stopPropagation()} />
          </Tippy>
        </div>
        <button
          style={{
            width: '28px',
            height: '28px',
            padding: 0,
            borderRadius: '6px',
            border: '1px solid var(--bk-c-d5d7da, #d5d7da)',
            background: 'var(--bk-c-ffffff, #ffffff)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
            outline: 'none'
          }}
          onClick={(e) => { e.stopPropagation(); onToggleExpand(); }}
        >
          <i className={`far fa-chevron-${expanded ? 'down' : 'right'}`}
             style={{ fontSize: '14px', color: 'var(--bk-c-1982c4, #1982C4)' }}></i>
        </button>
      </div>

      {expanded && (
        <div style={contentContainerStyle}>
          <div style={{ flex: '1 0 256px', display: 'flex', flexDirection: 'column' }}>
            {ticketLineItems.length > 0 && (
              <div style={{ height: 67, display: 'flex', alignItems: 'flex-end', marginBottom: 1 }}>
                <div style={{ width: '14px', flexShrink: 0 }} />
                <label style={inputLabelStyle}>Item</label>
              </div>
            )}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} modifiers={[restrictToVerticalAxis]}>
              <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
            {ticketLineItems.map((budgetLineItem, index) => (
              <SortableRow
                key={budgetLineItem.uuid || budgetLineItem.id}
                id={budgetLineItem.uuid || String(budgetLineItem.id)}
                style={{ height: rowHeight, marginTop: '-1px', display: 'flex' }}>
                <div style={{ position: 'relative', height: '100%', flex: 1 }}>
                  <input
                    type="text"
                    disabled={budgetLineItem.isFromSettlement}
                    style={{
                      ...(budgetLineItem.isFromSettlement ? disabledInputStyle : inputStyle),
                      height: '100%'
                    }}
                    value={budgetLineItem.name || ''}
                    onChange={(e) => handleFieldChange(budgetLineItem, 'name', e.target.value)}
                    onBlur={(e) => handleNameBlur(budgetLineItem, index, e.target.value)}
                    placeholder="Enter item label"
                    tabIndex={budgetLineItem.isFromSettlement ? -1 : getTabIndex(index, 'item')}
                  />
                  {!budgetLineItem.show_in_offer && (
                    <div style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)'
                    }}>
                      <Tippy
                        placement={'top-start'}
                        maxWidth={200}
                        animation={'shift-away'}
                        theme='material'
                        content={
                          <div style={{ fontSize: "10px" }}>
                            This row will be hidden in the offer sent to the artist
                            but values will still be included in any calculations.
                          </div>
                        }>
                        <i className="fas fa-eye-slash text-muted"
                           style={{ fontSize: '12px' }}></i>
                      </Tippy>
                    </div>
                  )}
                </div>
              </SortableRow>
            ))}
              </SortableContext>
            </DndContext>
            <button style={{ ...addButtonStyle, marginLeft: ticketLineItems.length > 0 ? '14px' : 0, marginTop: ticketLineItems.length > 0 ? '8px' : 0 }} onClick={handleAddRow}>
              <i className="far fa-plus-circle"></i>
              Add ticket
            </button>
          </div>

          {ticketLineItems.length > 0 && showOffer ? (
            <div style={columnBoxStyle}>
              <div style={{ ...columnHeaderStyle, height: headerHeight }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={columnTitleStyle}>Offer</span>
                  <Popup
                      open={allInOpen}
                      onOpen={() => setAllInOpen(true)}
                      onClose={() => setAllInOpen(false)}
                      modal
                      closeOnDocumentClick
                      contentStyle={{
                        border: 'none', borderRadius: '8px',
                        boxShadow: '0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)',
                        backgroundColor: 'var(--sl-bg-primary)',
                        width: '480px', maxWidth: 'calc(100vw - 16px)', padding: '0'
                      }}
                      trigger={
                        <button style={{
                          background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                          color: 'var(--sl-text-brand)', fontSize: '13px', fontWeight: 500
                        }}>
                          <i className="far fa-calculator" style={{ marginRight: '4px' }}></i>
                          All-in price
                        </button>
                      }
                    >
                      {close => (
                        <AllInPriceModal
                          team={team}
                          calendarEventId={offer?.calendar_event_id}
                          csrfToken={csrfToken}
                          initialPrice={ticketLineItems[0]?.left_hand}
                          onClose={close}
                        />
                      )}
                    </Popup>
                </div>
                <button
                  style={collapseButtonStyle}
                  onClick={() => onColumnVisibilityChange &&
                    onColumnVisibilityChange('show_offer_column', false)}
                >
                  <i className="fas fa-compress" style={{ color: 'var(--bk-c-535862, #535862)' }}></i>
                </button>
              </div>
              <div style={{ display: 'flex', height: labelRowHeight, marginBottom: 1, paddingBottom: 2 }}>
                <div style={{ width: offerAvailableWidth, marginRight: '-1px' }}>
                  <label style={inputLabelStyle}>Available</label>
                </div>
                <div style={{ width: offerCompsWidth, marginRight: '-1px' }}>
                  <label style={inputLabelStyle}>Comps</label>
                </div>
                <div style={{ width: offerKillsWidth, marginRight: '-1px' }}>
                  <label style={inputLabelStyle}>Kills</label>
                </div>
                <div style={{ width: offerSellableWidth, marginRight: '-1px' }}>
                  <label style={inputLabelStyle}>Sellable</label>
                </div>
                <div style={{ width: offerSalePriceWidth, marginRight: '-1px' }}>
                  <label style={inputLabelStyle}>Sale Price</label>
                </div>
                <div style={{
                  width: offerTotalWidth,
                  marginRight: showCurrencyConversion ? '-1px' : 0
                }}>
                  <label style={inputLabelStyle}>Total</label>
                </div>
                {showCurrencyConversion && globalCurrency && (
                  <div style={{ width: convertedSalePriceWidth }}>
                    <label style={inputLabelStyle}>{team.secondary_currency_symbol}</label>
                  </div>
                )}
              </div>
              {ticketLineItems.map((budgetLineItem, index) => {
                const calculatedRow = getCalculatedRow(budgetLineItem);
                const showConvertedInput = showCurrencyConversion && globalCurrency;
                return (
                <div key={budgetLineItem.uuid || budgetLineItem.id} style={{ display: 'flex', height: rowHeight, marginTop: '-1px' }}>
                  <div style={{ width: offerAvailableWidth, marginRight: '-1px' }}>
                    <CurrencyInput
                      disabled={budgetLineItem.isFromSettlement}
                      style={{
                        ...(budgetLineItem.isFromSettlement ? disabledInputStyle : inputStyle),
                        height: '100%',
                        textAlign: 'right'
                      }}
                      maskOptions={{ prefix: "", integerLimit: null, allowDecimal: false }}
                      placeholder="0"
                      value={isCleared(budgetLineItem, 'available') ? '' : budgetLineItem.available}
                      onFocus={handleZeroFocus(budgetLineItem, 'available', budgetLineItem.available, !budgetLineItem.isFromSettlement)}
                      onBlur={handleZeroBlur(budgetLineItem, 'available')}
                      onChange={(e) => handleFieldChange(budgetLineItem, 'available', parseNumber(e.target.value))}
                      tabIndex={budgetLineItem.isFromSettlement ? -1 : getTabIndex(index, 'available')}
                    />
                  </div>
                  <div style={{ width: offerCompsWidth, marginRight: '-1px' }}>
                    <CurrencyInput
                      disabled={budgetLineItem.isFromSettlement}
                      style={{
                        ...(budgetLineItem.isFromSettlement ? disabledInputStyle : inputStyle),
                        height: '100%',
                        textAlign: 'right'
                      }}
                      maskOptions={{ prefix: "", integerLimit: null, allowDecimal: false }}
                      placeholder="0"
                      value={isCleared(budgetLineItem, 'comps') ? '' : budgetLineItem.comps}
                      onFocus={handleZeroFocus(budgetLineItem, 'comps', budgetLineItem.comps, !budgetLineItem.isFromSettlement)}
                      onBlur={handleZeroBlur(budgetLineItem, 'comps')}
                      onChange={(e) => handleFieldChange(budgetLineItem, 'comps', parseNumber(e.target.value))}
                      tabIndex={budgetLineItem.isFromSettlement ? -1 : getTabIndex(index, 'comps')}
                    />
                  </div>
                  <div style={{ width: offerKillsWidth, marginRight: '-1px' }}>
                    <CurrencyInput
                      disabled={budgetLineItem.isFromSettlement}
                      style={{
                        ...(budgetLineItem.isFromSettlement ? disabledInputStyle : inputStyle),
                        height: '100%',
                        textAlign: 'right'
                      }}
                      maskOptions={{ prefix: "", integerLimit: null, allowDecimal: false }}
                      placeholder="0"
                      value={isCleared(budgetLineItem, 'kills') ? '' : budgetLineItem.kills}
                      onFocus={handleZeroFocus(budgetLineItem, 'kills', budgetLineItem.kills, !budgetLineItem.isFromSettlement)}
                      onBlur={handleZeroBlur(budgetLineItem, 'kills')}
                      onChange={(e) => handleFieldChange(budgetLineItem, 'kills', parseNumber(e.target.value))}
                      tabIndex={budgetLineItem.isFromSettlement ? -1 : getTabIndex(index, 'kills')}
                    />
                  </div>
                  <div style={{ width: offerSellableWidth, marginRight: '-1px' }}>
                    <CurrencyInput
                      disabled
                      style={{ ...disabledInputStyle, height: '100%', textAlign: 'right' }}
                      maskOptions={{ prefix: "", integerLimit: null, allowDecimal: false }}
                      value={budgetLineItem.right_hand < 0 ? 0 : Number(budgetLineItem.right_hand)}
                      tabIndex={-1}
                    />
                  </div>
                  <div style={{ width: offerSalePriceWidth, marginRight: '-1px' }}>
                    <CurrencyInput
                      disabled={budgetLineItem.isFromSettlement}
                      style={{
                        ...(budgetLineItem.isFromSettlement ? disabledInputStyle : inputStyle),
                        height: '100%',
                        textAlign: 'right'
                      }}
                      maskOptions={{ prefix: "$", integerLimit: null }}
                      placeholder="$0.00"
                      value={priceDisplayValue(budgetLineItem, 'left_hand', budgetLineItem.left_hand)}
                      onFocus={handlePriceFocus(budgetLineItem, 'left_hand', budgetLineItem.left_hand, !budgetLineItem.isFromSettlement)}
                      onBlur={handlePriceBlur(budgetLineItem, 'left_hand')}
                      onChange={handlePriceInput(budgetLineItem, 'left_hand', budgetLineItem.left_hand)}
                      tabIndex={budgetLineItem.isFromSettlement ? -1 : getTabIndex(index, 'salePrice')}
                    />
                  </div>
                  <div style={{
                    width: offerTotalWidth,
                    marginRight: showConvertedInput ? '-1px' : 0,
                    position: 'relative'
                  }}>
                    {hasRoundingMismatch(budgetLineItem, '', calculatedRow) && (
                      <Tippy content={`Calculated total is ${getCalculatedTotalValue('', calculatedRow)} due to rounding`}>
                        <span style={{
                          position: 'absolute',
                          left: '4px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: 'var(--bk-c-9ca3af, #9CA3AF)',
                          fontSize: '10px',
                          cursor: 'help',
                          zIndex: 1
                        }}>
                          <i className="fas fa-info-circle"></i>
                        </span>
                      </Tippy>
                    )}
                    <CurrencyInput
                      disabled={isTotalDisabledForTicket(budgetLineItem, '')}
                      style={{
                        ...(isTotalDisabledForTicket(budgetLineItem, '') ? disabledInputStyle : inputStyle),
                        height: '100%',
                        textAlign: 'right'
                      }}
                      maskOptions={{ prefix: "$", integerLimit: null }}
                      placeholder="$0.00"
                      value={getTotalDisplayValue(budgetLineItem, '', calculatedRow)}
                      onFocus={(e) => { handleSelectOnFocus(e); !isTotalDisabledForTicket(budgetLineItem, '') && handleTotalFocus(budgetLineItem, '', calculatedRow); }}
                      onChange={(e) => !isTotalDisabledForTicket(budgetLineItem, '') && handleTotalInputChange(
                        budgetLineItem.uuid,
                        '',
                        e.target.value.replace(/[\$,\s]/g, '')
                      )}
                      onBlur={() => !isTotalDisabledForTicket(budgetLineItem, '') && handleTotalBlur(budgetLineItem, '', calculatedRow)}
                      tabIndex={isTotalDisabledForTicket(budgetLineItem, '') ? -1 : getTabIndex(index, 'offerTotal')}
                    />
                  </div>
                  {showConvertedInput && (
                    <div style={{ width: convertedSalePriceWidth }}>
                      <CurrencyInput
                        disabled
                        style={{ ...disabledInputStyle, height: '100%', textAlign: 'right' }}
                        maskOptions={{ prefix: "$", integerLimit: null }}
                        value={calculatedRow?.calculatedTotal != null
                          ? globalCurrency.convert(calculatedRow.calculatedTotal).toFixed(2)
                          : ''
                        }
                      />
                    </div>
                  )}
                </div>
                );
              })}
              <div style={{ display: 'flex', height: rowHeight, alignItems: 'center' }}>
                <div style={{
                  width: offerAvailableWidth,
                  marginRight: '-1px',
                  textAlign: 'right',
                  fontWeight: '600',
                  fontSize: '14px',
                  paddingRight: '8px'
                }}>
                  {ticketsAvailableTotal.toLocaleString()}
                </div>
                <div style={{
                  width: offerCompsWidth,
                  marginRight: '-1px',
                  textAlign: 'right',
                  fontWeight: '600',
                  fontSize: '14px',
                  paddingRight: '8px'
                }}>
                  {ticketsCompsTotal.toLocaleString()}
                </div>
                <div style={{
                  width: offerKillsWidth,
                  marginRight: '-1px',
                  textAlign: 'right',
                  fontWeight: '600',
                  fontSize: '14px',
                  paddingRight: '8px'
                }}>
                  {ticketsKillsTotal.toLocaleString()}
                </div>
                <div style={{
                  width: offerSellableWidth,
                  marginRight: '-1px',
                  textAlign: 'right',
                  fontWeight: '600',
                  fontSize: '14px',
                  paddingRight: '8px'
                }}>
                  {ticketsSellableTotal.toLocaleString()}
                </div>
                <div style={{
                  width: offerSalePriceWidth,
                  marginRight: '-1px',
                  textAlign: 'right',
                  fontWeight: '600',
                  fontSize: '14px',
                  paddingRight: '8px'
                }}>
                  Total
                </div>
                <div style={{
                  width: offerTotalWidth,
                  textAlign: 'right',
                  fontWeight: '600',
                  fontSize: '14px',
                  paddingRight: '8px',
                  marginRight: showCurrencyConversion ? '-1px' : 0
                }}>
                  {formatCalculatedNumber(ticketsTotal)}
                </div>
                {showCurrencyConversion && globalCurrency && (
                  <div style={{
                    width: convertedSalePriceWidth,
                    textAlign: 'right',
                    fontWeight: '600',
                    fontSize: '14px',
                    paddingRight: '8px'
                  }}>
                    {globalCurrency.formatted(ticketsTotal)}
                  </div>
                )}
              </div>
            </div>
          ) : ticketLineItems.length > 0 ? (
            <div
              style={offerCollapsedStyle}
              onClick={() => onColumnVisibilityChange &&
                onColumnVisibilityChange('show_offer_column', true)}
            >
              <button style={collapseButtonStyle}>
                <i className="fas fa-expand" style={{ color: 'var(--bk-c-535862, #535862)' }}></i>
              </button>
              <span style={verticalTextStyle}>Offer</span>
            </div>
          ) : null}

          {ticketLineItems.length > 0 && showForecast ? (
            <div style={forecastExpandedStyle}>
              <div style={{ ...columnHeaderStyle, height: headerHeight }}>
                <span style={columnTitleStyle}>Forecast</span>
                <button
                  style={collapseButtonStyle}
                  onClick={() => onColumnVisibilityChange &&
                    onColumnVisibilityChange('show_forecast_column', false)}
                >
                  <i className="fas fa-compress" style={{ color: 'var(--bk-c-136395, #136395)' }}></i>
                </button>
              </div>
              <div style={{ display: 'flex', height: labelRowHeight, marginBottom: 1, paddingBottom: 2 }}>
                <div style={{ width: forecastQtyWidth, marginRight: '-1px' }}>
                  <label style={inputLabelStyle}>QTY</label>
                </div>
                <div style={{ width: forecastSalePriceWidth, marginRight: '-1px' }}>
                  <label style={inputLabelStyle}>Sale Price</label>
                </div>
                <div style={{
                  width: forecastTotalWidth,
                  marginRight: showCurrencyConversion ? '-1px' : 0
                }}>
                  <label style={inputLabelStyle}>Total</label>
                </div>
                {showCurrencyConversion && globalCurrency && (
                  <div style={{ width: convertedSalePriceWidth }}>
                    <label style={inputLabelStyle}>{team.secondary_currency_symbol}</label>
                  </div>
                )}
              </div>
              {ticketLineItems.map((budgetLineItem, index) => {
                const calculatedRow = getCalculatedRow(budgetLineItem);
                const showConvertedInput = showCurrencyConversion && globalCurrency;
                return (
                <div key={budgetLineItem.uuid || budgetLineItem.id} style={{ display: 'flex', height: rowHeight, marginTop: '-1px' }}>
                  <div style={{ width: forecastQtyWidth, marginRight: '-1px' }}>
                    <CurrencyInput
                      disabled={budgetLineItem.isFromSettlement}
                      style={{
                        ...(budgetLineItem.isFromSettlement ? disabledInputStyle : inputStyle),
                        height: '100%',
                        textAlign: 'right'
                      }}
                      maskOptions={{ prefix: "", integerLimit: null, allowDecimal: false }}
                      placeholder="0"
                      value={isCleared(budgetLineItem, 'right_hand_forecast') ? ''
                        : budgetLineItem.right_hand_forecast === ''
                          ? budgetLineItem.right_hand_forecast
                          : Number(budgetLineItem.right_hand_forecast)}
                      onFocus={handleZeroFocus(budgetLineItem, 'right_hand_forecast', budgetLineItem.right_hand_forecast, !budgetLineItem.isFromSettlement)}
                      onBlur={handleZeroBlur(budgetLineItem, 'right_hand_forecast')}
                      onChange={(e) => handleFieldChange(budgetLineItem, 'right_hand_forecast',
                        e.target.value.length > 0 ? e.target.value.replace(/,/g, '') : '')}
                      tabIndex={budgetLineItem.isFromSettlement ? -1 : getTabIndex(index, 'forecastQty')}
                    />
                  </div>
                  <div style={{ width: forecastSalePriceWidth, marginRight: '-1px' }}>
                    <CurrencyInput
                      disabled={budgetLineItem.isFromSettlement}
                      style={{
                        ...(budgetLineItem.isFromSettlement ? disabledInputStyle : inputStyle),
                        height: '100%',
                        textAlign: 'right'
                      }}
                      maskOptions={{ prefix: "$", integerLimit: null }}
                      placeholder="$0.00"
                      value={priceDisplayValue(budgetLineItem, 'left_hand_forecast', budgetLineItem.left_hand_forecast)}
                      onFocus={handlePriceFocus(budgetLineItem, 'left_hand_forecast', budgetLineItem.left_hand_forecast, !budgetLineItem.isFromSettlement)}
                      onBlur={handlePriceBlur(budgetLineItem, 'left_hand_forecast')}
                      onChange={handlePriceInput(budgetLineItem, 'left_hand_forecast', budgetLineItem.left_hand_forecast)}
                      tabIndex={budgetLineItem.isFromSettlement ? -1 : getTabIndex(index, 'forecastPrice')}
                    />
                  </div>
                  <div style={{
                    width: forecastTotalWidth,
                    marginRight: showConvertedInput ? '-1px' : 0,
                    position: 'relative'
                  }}>
                    {hasRoundingMismatch(budgetLineItem, '_forecast', calculatedRow) && (
                      <Tippy content={`Calculated total is ${getCalculatedTotalValue('_forecast', calculatedRow)} due to rounding`}>
                        <span style={{
                          position: 'absolute',
                          left: '4px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: 'var(--bk-c-9ca3af, #9CA3AF)',
                          fontSize: '10px',
                          cursor: 'help',
                          zIndex: 1
                        }}>
                          <i className="fas fa-info-circle"></i>
                        </span>
                      </Tippy>
                    )}
                    <CurrencyInput
                      disabled={isTotalDisabledForTicket(budgetLineItem, '_forecast')}
                      style={{
                        ...(isTotalDisabledForTicket(budgetLineItem, '_forecast') ? disabledInputStyle : inputStyle),
                        height: '100%',
                        textAlign: 'right'
                      }}
                      maskOptions={{ prefix: "$", integerLimit: null }}
                      placeholder="$0.00"
                      value={getTotalDisplayValue(budgetLineItem, '_forecast', calculatedRow)}
                      onFocus={(e) => { handleSelectOnFocus(e); !isTotalDisabledForTicket(budgetLineItem, '_forecast') && handleTotalFocus(budgetLineItem, '_forecast', calculatedRow); }}
                      onChange={(e) => !isTotalDisabledForTicket(budgetLineItem, '_forecast') && handleTotalInputChange(
                        budgetLineItem.uuid,
                        '_forecast',
                        e.target.value.replace(/[\$,\s]/g, '')
                      )}
                      onBlur={() => !isTotalDisabledForTicket(budgetLineItem, '_forecast') && handleTotalBlur(budgetLineItem, '_forecast', calculatedRow)}
                      tabIndex={isTotalDisabledForTicket(budgetLineItem, '_forecast') ? -1 : getTabIndex(index, 'forecastTotal')}
                    />
                  </div>
                  {showConvertedInput && (
                    <div style={{ width: convertedSalePriceWidth }}>
                      <CurrencyInput
                        disabled
                        style={{ ...disabledInputStyle, height: '100%', textAlign: 'right' }}
                        maskOptions={{ prefix: "$", integerLimit: null }}
                        value={calculatedRow?.calculatedForecastTotal != null
                          ? globalCurrency.convert(calculatedRow.calculatedForecastTotal).toFixed(2)
                          : ''
                        }
                      />
                    </div>
                  )}
                </div>
                );
              })}
              <div style={{ display: 'flex', height: rowHeight, alignItems: 'center' }}>
                <div style={{
                  width: forecastQtyWidth,
                  marginRight: '-1px',
                  textAlign: 'right',
                  fontWeight: '600',
                  fontSize: '14px',
                  paddingRight: '8px'
                }}>
                  {ticketsForecastQtyTotal.toLocaleString()}
                </div>
                <div style={{
                  width: forecastSalePriceWidth,
                  marginRight: '-1px',
                  textAlign: 'right',
                  fontWeight: '600',
                  fontSize: '14px',
                  paddingRight: '8px'
                }}>
                  Total
                </div>
                <div style={{
                  width: forecastTotalWidth,
                  textAlign: 'right',
                  fontWeight: '600',
                  fontSize: '14px',
                  paddingRight: '8px',
                  marginRight: showCurrencyConversion ? '-1px' : 0
                }}>
                  {formatCalculatedNumber(ticketsForecastTotal)}
                </div>
                {showCurrencyConversion && globalCurrency && (
                  <div style={{
                    width: convertedSalePriceWidth,
                    textAlign: 'right',
                    fontWeight: '600',
                    fontSize: '14px',
                    paddingRight: '8px'
                  }}>
                    {globalCurrency.formatted(ticketsForecastTotal)}
                  </div>
                )}
              </div>
            </div>
          ) : ticketLineItems.length > 0 && !hideCollapsedColumns ? (
            <div
              style={forecastCollapsedStyle}
              onClick={() => onColumnVisibilityChange &&
                onColumnVisibilityChange('show_forecast_column', true)}
            >
              <button style={collapseButtonStyle}>
                <i className="fas fa-expand" style={{ color: 'var(--bk-c-136395, #136395)' }}></i>
              </button>
              <span style={verticalTextStyle}>Forecast</span>
            </div>
          ) : null}

          {ticketLineItems.length > 0 && showActuals ? (
            <div style={actualsExpandedStyle}>
              <div style={{ ...columnHeaderStyle, height: headerHeight }}>
                <span style={columnTitleStyle}>Actuals</span>
                <button
                  style={collapseButtonStyle}
                  onClick={() => onColumnVisibilityChange &&
                    onColumnVisibilityChange('show_actuals_column', false)}
                >
                  <i className="fas fa-compress" style={{ color: 'var(--bk-c-155eef, #155EEF)' }}></i>
                </button>
              </div>
              <div style={{ display: 'flex', height: labelRowHeight, marginBottom: 1, paddingBottom: 2 }}>
                <div style={{ width: actualsQtyWidth, marginRight: '-1px' }}>
                  <label style={inputLabelStyle}>QTY</label>
                </div>
                <div style={{ width: actualsSalePriceWidth, marginRight: '-1px' }}>
                  <label style={inputLabelStyle}>Sale Price</label>
                </div>
                <div style={{
                  width: actualsTotalWidth,
                  marginRight: showCurrencyConversion ? '-1px' : 0
                }}>
                  <label style={inputLabelStyle}>Total</label>
                </div>
                {showCurrencyConversion && globalCurrency && (
                  <div style={{ width: convertedSalePriceWidth }}>
                    <label style={inputLabelStyle}>{team.secondary_currency_symbol}</label>
                  </div>
                )}
              </div>
              {ticketLineItems.map((budgetLineItem, index) => {
                const actualPrice = budgetLineItem.left_hand_actual || 0;
                const calculatedRow = getCalculatedRow(budgetLineItem);
                const showConvertedInput = showCurrencyConversion && globalCurrency;
                return (
                  <div key={budgetLineItem.uuid || budgetLineItem.id} style={{ display: 'flex', height: rowHeight, marginTop: '-1px' }}>
                    <div style={{ width: actualsQtyWidth, marginRight: '-1px' }}>
                      <CurrencyInput
                        disabled={budgetLineItem.isFromSettlement}
                        style={{
                          ...(budgetLineItem.isFromSettlement ? disabledInputStyle : inputStyle),
                          height: '100%',
                          textAlign: 'right'
                        }}
                        maskOptions={{ prefix: "", integerLimit: null, allowDecimal: false }}
                        placeholder="0"
                        value={isCleared(budgetLineItem, 'right_hand_actual') ? ''
                          : budgetLineItem.right_hand_actual === '' || budgetLineItem.right_hand_actual == null
                            ? (budgetLineItem.right_hand_actual || '')
                            : Number(budgetLineItem.right_hand_actual)}
                        onFocus={handleZeroFocus(budgetLineItem, 'right_hand_actual', budgetLineItem.right_hand_actual, !budgetLineItem.isFromSettlement)}
                        onBlur={handleZeroBlur(budgetLineItem, 'right_hand_actual')}
                        onChange={(e) => handleFieldChange(budgetLineItem, 'right_hand_actual',
                          e.target.value.length > 0 ? e.target.value.replace(/,/g, '') : '')}
                        tabIndex={budgetLineItem.isFromSettlement
                          ? -1
                          : getTabIndex(index, 'actualsQty')}
                      />
                    </div>
                    <div style={{ width: actualsSalePriceWidth, marginRight: '-1px' }}>
                      <CurrencyInput
                        disabled={budgetLineItem.isFromSettlement}
                        style={{
                          ...(budgetLineItem.isFromSettlement ? disabledInputStyle : inputStyle),
                          height: '100%',
                          textAlign: 'right'
                        }}
                        maskOptions={{ prefix: "$", integerLimit: null }}
                        placeholder="$0.00"
                        value={priceDisplayValue(budgetLineItem, 'left_hand_actual', actualPrice)}
                        onFocus={handlePriceFocus(budgetLineItem, 'left_hand_actual', actualPrice, !budgetLineItem.isFromSettlement)}
                        onBlur={handlePriceBlur(budgetLineItem, 'left_hand_actual')}
                        onChange={handlePriceInput(budgetLineItem, 'left_hand_actual', actualPrice)}
                        tabIndex={budgetLineItem.isFromSettlement
                          ? -1
                          : getTabIndex(index, 'actualsPrice')}
                      />
                    </div>
                    <div style={{
                      width: actualsTotalWidth,
                      marginRight: showConvertedInput ? '-1px' : 0,
                      position: 'relative'
                    }}>
                      {hasRoundingMismatch(budgetLineItem, '_actual', calculatedRow) && (
                        <Tippy content={`Calculated total is ${getCalculatedTotalValue('_actual', calculatedRow)} due to rounding`}>
                          <span style={{
                            position: 'absolute',
                            left: '4px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: 'var(--bk-c-9ca3af, #9CA3AF)',
                            fontSize: '10px',
                            cursor: 'help',
                            zIndex: 1
                          }}>
                            <i className="fas fa-info-circle"></i>
                          </span>
                        </Tippy>
                      )}
                      <CurrencyInput
                        disabled={isTotalDisabledForTicket(budgetLineItem, '_actual')}
                        style={{
                          ...(isTotalDisabledForTicket(budgetLineItem, '_actual') ? disabledInputStyle : inputStyle),
                          height: '100%',
                          textAlign: 'right'
                        }}
                        maskOptions={{ prefix: "$", integerLimit: null }}
                        placeholder="$0.00"
                        value={getTotalDisplayValue(budgetLineItem, '_actual', calculatedRow)}
                        onFocus={(e) => { handleSelectOnFocus(e); !isTotalDisabledForTicket(budgetLineItem, '_actual') && handleTotalFocus(budgetLineItem, '_actual', calculatedRow); }}
                        onChange={(e) => !isTotalDisabledForTicket(budgetLineItem, '_actual') && handleTotalInputChange(
                          budgetLineItem.uuid,
                          '_actual',
                          e.target.value.replace(/[\$,\s]/g, '')
                        )}
                        onBlur={() => !isTotalDisabledForTicket(budgetLineItem, '_actual') && handleTotalBlur(budgetLineItem, '_actual', calculatedRow)}
                        tabIndex={isTotalDisabledForTicket(budgetLineItem, '_actual') ? -1 : getTabIndex(index, 'actualsTotal')}
                      />
                    </div>
                    {showConvertedInput && (
                      <div style={{ width: convertedSalePriceWidth }}>
                        <CurrencyInput
                          disabled
                          style={{ ...disabledInputStyle, height: '100%', textAlign: 'right' }}
                          maskOptions={{ prefix: "$", integerLimit: null }}
                          value={calculatedRow?.calculatedActualTotal != null
                            ? globalCurrency.convert(calculatedRow.calculatedActualTotal).toFixed(2)
                            : ''
                          }
                        />
                      </div>
                    )}
                  </div>
                );
              })}
              <div style={{ display: 'flex', height: rowHeight, alignItems: 'center' }}>
                <div style={{
                  width: actualsQtyWidth,
                  marginRight: '-1px',
                  textAlign: 'right',
                  fontWeight: '600',
                  fontSize: '14px',
                  paddingRight: '8px'
                }}>
                  {ticketsActualsQtyTotal.toLocaleString()}
                </div>
                <div style={{
                  width: actualsSalePriceWidth,
                  marginRight: '-1px',
                  textAlign: 'right',
                  fontWeight: '600',
                  fontSize: '14px',
                  paddingRight: '8px'
                }}>
                  Total
                </div>
                <div style={{
                  width: actualsTotalWidth,
                  textAlign: 'right',
                  fontWeight: '600',
                  fontSize: '14px',
                  paddingRight: '8px',
                  marginRight: showCurrencyConversion ? '-1px' : 0
                }}>
                  {formatCalculatedNumber(ticketsActualsTotal)}
                </div>
                {showCurrencyConversion && globalCurrency && (
                  <div style={{
                    width: convertedSalePriceWidth,
                    textAlign: 'right',
                    fontWeight: '600',
                    fontSize: '14px',
                    paddingRight: '8px'
                  }}>
                    {globalCurrency.formatted(ticketsActualsTotal)}
                  </div>
                )}
              </div>
            </div>
          ) : ticketLineItems.length > 0 && !hideCollapsedColumns ? (
            <div
              style={actualsCollapsedStyle}
              onClick={() => onColumnVisibilityChange &&
                onColumnVisibilityChange('show_actuals_column', true)}
            >
              <button style={collapseButtonStyle}>
                <i className="fas fa-expand" style={{ color: 'var(--bk-c-155eef, #155EEF)' }}></i>
              </button>
              <span style={verticalTextStyle}>Actuals</span>
            </div>
          ) : null}

          {ticketLineItems.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', width: isMobile ? '64px' : '14px', alignItems: 'center' }}>
              <div style={{ height: totalHeaderHeight }}></div>
              {ticketLineItems.map((budgetLineItem, index) => (
                <div key={budgetLineItem.uuid || budgetLineItem.id} style={{ height: rowHeight, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '-1px' }}>
                  {budgetLineItem.isFromSettlement ? (
                    <Tippy
                      placement="left"
                      maxWidth={200}
                      animation="shift-away"
                      theme="material"
                      content={
                        <div style={{ fontSize: "10px" }}>
                          This row was automatically added from ticket sales
                          and cannot be edited.
                        </div>
                      }
                    >
                      <i className="fas fa-info-circle text-muted"
                         style={{ fontSize: '12px', cursor: 'help' }}></i>
                    </Tippy>
                  ) : (
                    <TicketRowMenu
                      csrfToken={csrfToken}
                      team={team}
                      budget={budget}
                      budgetLineItem={budgetLineItem}
                      updateBudget={updateBudget}
                      autoSaveBudgetLineItem={autoSaveBudgetLineItem}
                      onDeleteRow={handleDeleteRow}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

TicketsSection.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  offer: PropTypes.object.isRequired,
  budget: PropTypes.object.isRequired,
  calculated: PropTypes.object,
  updateBudget: PropTypes.func.isRequired,
  createTicketBudgetLineItem: PropTypes.func.isRequired,
  deleteBudgetLineItem: PropTypes.func.isRequired,
  autoSaveBudgetLineItem: PropTypes.func.isRequired,
  formatCalculatedNumber: PropTypes.func.isRequired,
  expanded: PropTypes.bool,
  onToggleExpand: PropTypes.func,
  settlementTickets: PropTypes.array,
  showOfferColumn: PropTypes.bool,
  showForecastColumn: PropTypes.bool,
  showActualsColumn: PropTypes.bool,
  hideCollapsedColumns: PropTypes.bool,
  onColumnVisibilityChange: PropTypes.func,
  sectionTabIndexOffset: PropTypes.number,
  showCurrencyConversion: PropTypes.bool,
  globalCurrency: PropTypes.object
};

export default TicketsSection;
